/* Funduino Startseite — das gesamte Verhalten der dreizehn Startseiten-Sektionen.
 *
 * Eine Datei, kein Framework. Alles steckt in Custom Elements, und das ist der
 * Grund: Der Theme-Editor rendert eine Section bei jeder Aenderung neu. Ein
 * Skript, das beim Laden einmal querySelector-t und Hoerer haengt, verliert
 * dabei jede Bindung — man muesste auf 'shopify:section:load' hoeren und
 * aufraeumen. Custom Elements upgraden sich von selbst, sobald ihr Tag im DOM
 * auftaucht, und raeumen in disconnectedCallback auf. Keine Sonderbehandlung
 * fuer den Editor noetig.
 *
 * Grundsatz wie in assets/fu-blog.js: KEINE Beschriftung steht hier im Code.
 * Alle Texte kommen aus data-Attributen, die das Liquid aus den
 * Section-Settings fuellt. Sonst waeren sie nicht mehr redaktionell aenderbar.
 *
 * Eine Abhaengigkeit, aus dem Theme selbst ueber die Importmap
 * (snippets/scripts.liquid):
 *
 *   @shopify/events  CartLinesUpdateEvent und CartErrorEvent. Ohne sie legt ein
 *     Klick zwar in den Korb, aber Korb-Symbol und Drawer merken es nicht.
 *
 * Bis zum 17.09.2026 stand hier ausserdem fu-st-lazy, das ein Widget erst beim
 * Sichtbarwerden einsetzte, und dafuer getIntersectionRoot aus
 * @theme/scroll-container. Beides ist mit sections/fu-kundenstimmen.liquid
 * weggefallen — die Kundenstimmen laufen jetzt ueber Horizons custom-liquid
 * mit dem Code der Bewertungs-App. Wer verzoegertes Laden zurueckholt, findet
 * die Fassung in der Git-Historie; wichtig daran war der ausdrueckliche root,
 * weil Horizon ab 990px .page-wrapper scrollt und nicht das Fenster.
 *
 * Die Datei ist ein Modul und wird von jeder Sektion mit type="module" geladen.
 * Mehrfaches Laden derselben URL fuehrt der Browser zusammen — der Code laeuft
 * genau einmal.
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/* ── Kleinkram ─────────────────────────────────────────────────────────── */

const WENIGER_BEWEGUNG = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Registriert ein Element nur, wenn der Name noch frei ist.
 *  Der Theme-Editor kann dieselbe Moduldatei in seltenen Faellen erneut
 *  auswerten; ein zweites define() waere ein harter Fehler und wuerde alles
 *  danach verschlucken. */
function anmelden(name, klasse) {
  if (!customElements.get(name)) customElements.define(name, klasse);
}

/** Zwei Ziffern, ohne Beschriftung. Fuer den Countdown. */
function zweistellig(n) {
  return String(Math.max(0, n)).padStart(2, '0');
}

/* ── fu-st-countdown ───────────────────────────────────────────────────────
 * Aktionsleiste. Zaehlt auf data-ende herunter und blendet die Leiste danach
 * aus, wenn data-nach-ablauf="ausblenden" steht.
 *
 * Der Timer laeuft sekundenweise. Das ist vertretbar, weil er nur solange
 * laeuft, wie die Leiste im DOM steht, und disconnectedCallback ihn beendet —
 * im Theme-Editor also nicht mit jeder Neuzeichnung ein weiterer dazukommt.
 */
class FuCountdown extends HTMLElement {
  connectedCallback() {
    const roh = this.getAttribute('data-ende');
    this.ende = roh ? new Date(roh).getTime() : NaN;

    /* Kein oder kaputtes Datum: Die Leiste bleibt einfach stehen, ohne Uhr.
       Ein leeres Enddatum ist der Normalfall der reinen Textleiste. */
    if (!Number.isFinite(this.ende)) {
      this.hidden = false;
      return;
    }

    this.felder = {
      tage: this.querySelector('[data-fu-feld="tage"]'),
      stunden: this.querySelector('[data-fu-feld="stunden"]'),
      minuten: this.querySelector('[data-fu-feld="minuten"]'),
      sekunden: this.querySelector('[data-fu-feld="sekunden"]'),
    };

    this.zeichnen();
    this.timer = window.setInterval(() => this.zeichnen(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  zeichnen() {
    const rest = this.ende - Date.now();

    if (rest <= 0) {
      window.clearInterval(this.timer);
      if (this.getAttribute('data-nach-ablauf') === 'ausblenden') {
        /* hidden am Host. Die Sektion selbst bleibt im DOM, damit der
           Theme-Editor sie weiter anspringen kann — im Editor blendet das
           Liquid sie ohnehin nie aus. */
        this.hidden = true;
        this.setAttribute('data-fu-abgelaufen', 'true');
        return;
      }
      this.setzen(0, 0, 0, 0);
      return;
    }

    const s = Math.floor(rest / 1000);
    this.setzen(Math.floor(s / 86400), Math.floor((s % 86400) / 3600), Math.floor((s % 3600) / 60), s % 60);
  }

  setzen(t, h, m, s) {
    if (this.felder.tage) this.felder.tage.textContent = String(t);
    if (this.felder.stunden) this.felder.stunden.textContent = zweistellig(h);
    if (this.felder.minuten) this.felder.minuten.textContent = zweistellig(m);
    if (this.felder.sekunden) this.felder.sekunden.textContent = zweistellig(s);
  }
}
anmelden('fu-st-countdown', FuCountdown);

/* ── fu-st-hero-slider ─────────────────────────────────────────────────────
 * Folgeschau im Hero. Die Folien liegen gestapelt; gewechselt wird ueber
 * data-fu-aktiv und inert.
 *
 * inert ist hier das Entscheidende und nicht bloss Kosmetik: Eine Folie, die
 * nur durchsichtig ist, bleibt fuer Tabulator und Screenreader vorhanden. Ohne
 * inert liefe die Tabulatorkette durch bis zu fuenf unsichtbare Foliensaetze
 * mit je zwei Knoepfen.
 *
 * Die Schau haelt an bei: prefers-reduced-motion, Hover, Fokus im Element und
 * verborgenem Tab (visibilitychange). Alle vier, weil jeder einzeln Grund
 * genug ist und zusammen kein Sonderfall entsteht.
 */
class FuHeroSlider extends HTMLElement {
  connectedCallback() {
    this.folien = Array.from(this.querySelectorAll('[data-fu-folie]'));
    if (this.folien.length === 0) return;

    this.punkte = Array.from(this.querySelectorAll('[data-fu-punkt]'));
    this.index = 0;
    this.intervall = Number(this.getAttribute('data-intervall')) || 6000;
    this.autoplay = this.getAttribute('data-autoplay') !== 'false' && this.folien.length > 1;

    this.zeigen(0);

    this.aufVor = () => this.weiter(1);
    this.aufZurueck = () => this.weiter(-1);
    this.querySelectorAll('[data-fu-vor]').forEach((b) => b.addEventListener('click', this.aufVor));
    this.querySelectorAll('[data-fu-zurueck]').forEach((b) => b.addEventListener('click', this.aufZurueck));

    this.punkte.forEach((p, i) => p.addEventListener('click', () => { this.zeigen(i); this.neuStarten(); }));

    /* Einzelne Folie: Knoepfe und Punkte verstecken statt tot stehen lassen. */
    if (this.folien.length < 2) {
      this.querySelectorAll('[data-fu-vor],[data-fu-zurueck],[data-fu-punkte]').forEach((el) => { el.hidden = true; });
      return;
    }

    this.anhalten = () => this.stoppen();
    this.weiterlaufen = () => this.starten();
    this.addEventListener('pointerenter', this.anhalten);
    this.addEventListener('pointerleave', this.weiterlaufen);
    this.addEventListener('focusin', this.anhalten);
    this.addEventListener('focusout', this.weiterlaufen);

    this.aufSichtbarkeit = () => (document.hidden ? this.stoppen() : this.starten());
    document.addEventListener('visibilitychange', this.aufSichtbarkeit);

    this.aufBewegung = () => (WENIGER_BEWEGUNG.matches ? this.stoppen() : this.starten());
    WENIGER_BEWEGUNG.addEventListener('change', this.aufBewegung);

    this.starten();
  }

  disconnectedCallback() {
    this.stoppen();
    document.removeEventListener('visibilitychange', this.aufSichtbarkeit);
    WENIGER_BEWEGUNG.removeEventListener('change', this.aufBewegung);
  }

  starten() {
    if (!this.autoplay || WENIGER_BEWEGUNG.matches || document.hidden) return;
    this.stoppen();
    this.timer = window.setInterval(() => this.weiter(1), this.intervall);
  }

  stoppen() {
    window.clearInterval(this.timer);
  }

  neuStarten() {
    this.stoppen();
    this.starten();
  }

  weiter(richtung) {
    this.zeigen((this.index + richtung + this.folien.length) % this.folien.length);
    this.neuStarten();
  }

  zeigen(i) {
    this.index = i;
    this.folien.forEach((f, j) => {
      const an = j === i;
      f.setAttribute('data-fu-aktiv', an ? 'true' : 'false');
      f.inert = !an;
    });
    this.punkte.forEach((p, j) => {
      const an = j === i;
      p.setAttribute('aria-current', an ? 'true' : 'false');
      p.setAttribute('data-fu-aktiv', an ? 'true' : 'false');
    });
    this.setAttribute('data-fu-index', String(i));
  }
}
anmelden('fu-st-hero-slider', FuHeroSlider);

/* ── fu-st-scroller ────────────────────────────────────────────────────────
 * Wischleiste mit Pfeilen. Gescrollt wird [data-fu-spur].
 *
 * Die Pfeile verschwinden, wenn nichts zu scrollen ist — nicht disabled,
 * sondern hidden. Ein deaktivierter Knopf, der nie wieder aktiv wird, ist auf
 * Touch nur Platzverbrauch; auf Touch wischt man ohnehin.
 *
 * Gescrollt wird um die sichtbare Breite minus eine Kartenbreite, damit die
 * letzte sichtbare Karte als Anker stehen bleibt.
 */
class FuScroller extends HTMLElement {
  connectedCallback() {
    this.spur = this.querySelector('[data-fu-spur]');
    if (!this.spur) return;

    /* Pfeile ueber das Element selbst, nicht an den Knoepfen: Der Neuaufbau
       der Warenkorb-Schublade (Morph) kann sie ersetzen. */
    this.aufKlick = (e) => {
      const pfeil = e.target instanceof Element ? e.target.closest('[data-fu-vor], [data-fu-zurueck]') : null;
      if (pfeil) this.schieben(pfeil.hasAttribute('data-fu-vor') ? 1 : -1);
    };
    this.addEventListener('click', this.aufKlick);

    this.aufScroll = () => this.zustand();
    this.spur.addEventListener('scroll', this.aufScroll, { passive: true });

    this.beobachter = new ResizeObserver(() => this.zustand());
    this.beobachter.observe(this.spur);

    /* Der Morph setzt die Pfeile auf den ausgelieferten Stand (hidden)
       zurueck und tauscht Karten, ohne dass sich die Spur in der Groesse
       aendert — der ResizeObserver merkt davon nichts. In der Schublade
       blieben die Pfeile so nach jeder Mengenaenderung weg (Gegenpruefung
       zum Volltest 02.11.2026). zustand() schreibt nur bei Abweichung,
       sonst loeste jede Pruefung die naechste aus. */
    this.morphWache = new MutationObserver(() => this.zustand());
    this.morphWache.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden', 'data-fu-scrollbar', 'data-fu-am-anfang', 'data-fu-am-ende'],
    });

    this.zustand();
  }

  disconnectedCallback() {
    this.beobachter?.disconnect();
    this.morphWache?.disconnect();
    this.spur?.removeEventListener('scroll', this.aufScroll);
    this.removeEventListener('click', this.aufKlick);
  }

  schieben(richtung) {
    const karte = this.spur.querySelector('[data-fu-karte]');
    const schritt = karte
      ? Math.max(karte.offsetWidth, this.spur.clientWidth - karte.offsetWidth)
      : this.spur.clientWidth * 0.8;
    this.spur.scrollBy({
      left: richtung * schritt,
      behavior: WENIGER_BEWEGUNG.matches ? 'auto' : 'smooth',
    });
  }

  zustand() {
    const { scrollLeft, scrollWidth, clientWidth } = this.spur;
    /* 2px Toleranz: Subpixel-Breiten lassen scrollLeft sonst nie exakt das
       Ende erreichen, und der Vor-Pfeil bliebe fuer immer sichtbar. */
    const scrollbar = scrollWidth - clientWidth > 2;
    const amAnfang = scrollLeft <= 2;
    const amEnde = scrollLeft >= scrollWidth - clientWidth - 2;

    const merkmal = (name, wert) => {
      if (this.getAttribute(name) !== wert) this.setAttribute(name, wert);
    };
    merkmal('data-fu-scrollbar', scrollbar ? 'true' : 'false');
    merkmal('data-fu-am-anfang', amAnfang ? 'true' : 'false');
    merkmal('data-fu-am-ende', amEnde ? 'true' : 'false');

    const zeigen = (knopf, weg) => {
      if (knopf && knopf.hidden !== weg) knopf.hidden = weg;
    };
    zeigen(this.querySelector('[data-fu-zurueck]'), !scrollbar || amAnfang);
    zeigen(this.querySelector('[data-fu-vor]'), !scrollbar || amEnde);
  }
}
anmelden('fu-st-scroller', FuScroller);

/* ── fu-st-tabs ────────────────────────────────────────────────────────────
 * Reiter nach dem ARIA-Muster "Tabs with Manual Activation": Pfeiltasten
 * bewegen den Fokus und schalten mit um, Home und End springen an die Enden.
 *
 * Bewusst automatische Aktivierung (der Fokus schaltet gleich um): Die Panels
 * stehen bereits im DOM, es wird nichts nachgeladen, also kostet ein Wechsel
 * nichts. Das ist der Fall, fuer den die WAI-ARIA-Practices automatische
 * Aktivierung ausdruecklich empfehlen.
 */
class FuTabs extends HTMLElement {
  connectedCallback() {
    this.reiter = Array.from(this.querySelectorAll('[role="tab"]'));
    if (this.reiter.length === 0) return;

    this.aufKlick = (e) => {
      const r = e.currentTarget;
      this.aktivieren(this.reiter.indexOf(r));
    };
    this.aufTaste = (e) => this.taste(e);

    this.reiter.forEach((r) => {
      r.addEventListener('click', this.aufKlick);
      r.addEventListener('keydown', this.aufTaste);
    });

    const start = this.reiter.findIndex((r) => r.getAttribute('aria-selected') === 'true');
    this.aktivieren(start < 0 ? 0 : start, false);
  }

  taste(e) {
    const i = this.reiter.indexOf(e.currentTarget);
    let ziel = -1;
    if (e.key === 'ArrowRight') ziel = (i + 1) % this.reiter.length;
    else if (e.key === 'ArrowLeft') ziel = (i - 1 + this.reiter.length) % this.reiter.length;
    else if (e.key === 'Home') ziel = 0;
    else if (e.key === 'End') ziel = this.reiter.length - 1;
    if (ziel < 0) return;
    e.preventDefault();
    this.aktivieren(ziel);
    this.reiter[ziel].focus();
  }

  aktivieren(index, melden = true) {
    this.reiter.forEach((r, j) => {
      const an = j === index;
      r.setAttribute('aria-selected', an ? 'true' : 'false');
      r.setAttribute('tabindex', an ? '0' : '-1');
      const panel = document.getElementById(r.getAttribute('aria-controls') || '');
      if (panel) panel.hidden = !an;
    });
    if (melden) this.dispatchEvent(new CustomEvent('fu-st-reiter', { bubbles: true, detail: { index } }));
  }
}
anmelden('fu-st-tabs', FuTabs);

/* ── fu-st-add ─────────────────────────────────────────────────────────────
 * Der Plus-Knopf an der Produktkarte. Legt in den Korb und wird kurz zum Haken.
 *
 * Der Weg ueber CartLinesUpdateEvent ist das Hausmuster dieses Themes und
 * steht so schon in assets/fu-blog.js. Er ist noetig, weil Horizons
 * Korb-Symbol, Korb-Drawer und Versandschwelle nicht am fetch haengen, sondern
 * an diesem Event. Ein blosser POST auf cart/add.js legt zwar in den Korb, der
 * Kunde sieht davon aber nichts, bis er die Seite neu laedt.
 *
 * Keine Beschriftung im Code: add, laden, ok und fehler kommen als
 * data-label-* vom Liquid.
 */
class FuAdd extends HTMLElement {
  connectedCallback() {
    this.knopf = this.querySelector('[data-fu-add]');
    if (!this.knopf) return;
    this.status = this.querySelector('[data-fu-status]');
    this.aufKlick = (e) => this.legen(e);
    this.knopf.addEventListener('click', this.aufKlick);

    /* Der Kartenfuss liefert seinen Knopf IMMER gesperrt aus und gibt hier
       frei — derselbe Weg wie beim Variantenwaehler, damit ohne dieses
       Skript kein Knopf dasteht, der nichts tut (Begruendung im Kopf von
       snippets/fu-karte-fuss.liquid). Das Attribut entscheidet, nicht
       disabled selbst: disabled traegt der Knopf auch waehrend des Ladens
       und bei ausverkaufter Variante.

       Andere Aufrufer von <fu-st-add> (sections/fu-cube.liquid,
       sections/fu-angebot-der-woche.liquid) tragen data-fu-kaufbar nicht und
       liefern ihren Knopf offen aus — fuer sie aendert sich nichts. */
    if (this.getAttribute('data-fu-kaufbar') === '1') this.knopf.disabled = false;
  }

  disconnectedCallback() {
    this.knopf?.removeEventListener('click', this.aufKlick);
    window.clearTimeout(this.rueckstellung);
  }

  label(name) {
    return this.getAttribute('data-label-' + name) || '';
  }

  zustand(name) {
    this.setAttribute('data-fu-zustand', name);
    if (this.status) this.status.textContent = this.label(name);
    const beschriftung = this.knopf.querySelector('[data-fu-label]');
    if (beschriftung) beschriftung.textContent = this.label(name);
    this.knopf.setAttribute('aria-label', this.label(name) || this.knopf.getAttribute('aria-label') || '');
  }

  sektionsIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      const id = el.getAttribute('data-section-id');
      if (id) ids.push(id);
    });
    return ids;
  }

  async legen(e) {
    e.preventDefault();
    if (this.knopf.disabled) return;

    const id = this.getAttribute('data-variante-id');
    if (!id) return;
    const menge = Number(this.getAttribute('data-menge')) || 1;

    this.knopf.disabled = true;
    this.zustand('laden');

    const zusage = CartLinesUpdateEvent.createPromise();
    this.knopf.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(id), quantity: menge }],
        promise: zusage.promise,
      })
    );

    let schief = false;
    let antwort = null;
    try {
      const r = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: menge }], sections: this.sektionsIds().join(',') }),
      });
      antwort = await r.json();
      schief = !!antwort.status;

      if (schief) {
        /* Der Grund geht sofort hinaus. Die englische Huelle in error wird von
           assets/fu-cart-error.js gegen den deutschen Rueckfalltext getauscht. */
        this.knopf.dispatchEvent(
          new CartErrorEvent({
            error: antwort.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: antwort.description, errors: antwort.errors },
          })
        );
      }
    } catch (err) {
      schief = true;
    }

    let korb = null;
    try {
      const r = await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (r.ok) korb = await r.json();
    } catch (err) {
      /* Der Korb liess sich nicht nachladen. Die Zusage muss trotzdem
         aufgeloest werden, sonst wartet Horizons Drawer fuer immer. */
    }

    zusage.resolve({
      cart: korb ? CartLinesUpdateEvent.createCartFromAjaxResponse(korb) : null,
      detail: {
        items: korb ? korb.items : [],
        source: 'fu-startseite',
        sourceId: this.getAttribute('id') || 'fu-st-add',
        itemCount: menge,
        sections: schief ? null : antwort && antwort.sections,
        didError: schief,
      },
    });

    this.zustand(schief ? 'fehler' : 'ok');
    this.knopf.disabled = false;
    window.clearTimeout(this.rueckstellung);
    this.rueckstellung = window.setTimeout(() => this.zustand('add'), 2400);
  }
}
anmelden('fu-st-add', FuAdd);

/* ── fu-st-expand ──────────────────────────────────────────────────────────
 * Marken-Ausklappen. Deckelt [data-fu-inhalt] auf data-hoehe-zu und klappt auf
 * Knopfdruck auf die volle Hoehe auf.
 *
 * Die Hoehe wird als Inline-max-height gesetzt und nicht ueber eine Klasse,
 * weil die eingeklappte Hoehe eine Einstellung ist und daher nur zur Laufzeit
 * bekannt ist. Beim Aufklappen wird auf die gemessene scrollHeight animiert
 * und danach auf none gesetzt — sonst schneidet ein spaeter nachgeladenes
 * Logo unten ab.
 */
class FuExpand extends HTMLElement {
  connectedCallback() {
    this.inhalt = this.querySelector('[data-fu-inhalt]');
    this.schalter = this.querySelector('[data-fu-schalter]');
    if (!this.inhalt || !this.schalter) return;

    this.text = this.querySelector('[data-fu-schalter-text]') || this.schalter;
    this.zu = Number(this.getAttribute('data-hoehe-zu')) || 220;
    this.offen = false;

    this.aufKlick = () => this.umschalten();
    this.schalter.addEventListener('click', this.aufKlick);

    /* Nur die eigene Hoehenanimation zaehlt. transitionend blubbert: Die
       Hover-Uebergaenge der Kacheln steigen aus [data-fu-inhalt] auf, und ein
       Uebergang am Pseudoelement ::after feuert ohnehin am Ursprungselement
       selbst. Ungefiltert beendete der erste davon die Auffahrt vorzeitig. */
    this.aufEnde = (e) => {
      if (e.target !== this.inhalt || e.propertyName !== 'max-height') return;
      if (this.offen) this.inhalt.style.maxHeight = 'none';
    };
    this.inhalt.addEventListener('transitionend', this.aufEnde);

    this.beobachter = new ResizeObserver(() => this.pruefen());
    this.beobachter.observe(this.inhalt);
    this.zeichnen();
    this.pruefen();
  }

  disconnectedCallback() {
    this.beobachter?.disconnect();
    this.schalter?.removeEventListener('click', this.aufKlick);
    this.inhalt?.removeEventListener('transitionend', this.aufEnde);
  }

  /** Passt alles ohnehin in die eingeklappte Hoehe, faellt der Schalter weg. */
  pruefen() {
    if (this.offen) return;
    const noetig = this.inhalt.scrollHeight > this.zu + 4;
    this.schalter.hidden = !noetig;
    this.setAttribute('data-fu-noetig', noetig ? 'true' : 'false');
  }

  umschalten() {
    this.offen = !this.offen;
    this.zeichnen();
  }

  zeichnen() {
    this.schalter.setAttribute('aria-expanded', this.offen ? 'true' : 'false');
    this.setAttribute('data-fu-offen', this.offen ? 'true' : 'false');
    this.text.textContent =
      this.getAttribute(this.offen ? 'data-text-weniger' : 'data-text-mehr') || this.text.textContent;

    if (this.offen) {
      this.inhalt.style.maxHeight = WENIGER_BEWEGUNG.matches ? 'none' : this.inhalt.scrollHeight + 'px';
    } else {
      /* Von 'none' aus animiert nichts. Erst die gemessene Hoehe setzen, einen
         Frame warten, dann zuklappen. */
      if (this.inhalt.style.maxHeight === 'none') {
        this.inhalt.style.maxHeight = this.inhalt.scrollHeight + 'px';
        void this.inhalt.offsetHeight;
      }
      this.inhalt.style.maxHeight = this.zu + 'px';
    }
  }
}
anmelden('fu-st-expand', FuExpand);

/* ── fu-st-video ───────────────────────────────────────────────────────────
 * Video mit Posterbild und eigenem Abspielknopf. Kein Autoplay: Das Video
 * traegt hier Inhalt und keine Stimmung, und ein von selbst startendes Video
 * ist auf Mobilfunk teuer. preload="none" steht im Markup.
 */
class FuVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('[data-fu-video]');
    this.knopf = this.querySelector('[data-fu-play]');
    if (!this.video || !this.knopf) return;

    this.aufKlick = () => {
      this.setAttribute('data-fu-laeuft', 'true');
      this.video.controls = true;
      const p = this.video.play();
      if (p && typeof p.catch === 'function') p.catch(() => this.removeAttribute('data-fu-laeuft'));
    };
    this.knopf.addEventListener('click', this.aufKlick);

    this.aufEnde = () => this.removeAttribute('data-fu-laeuft');
    this.video.addEventListener('ended', this.aufEnde);
  }

  disconnectedCallback() {
    this.knopf?.removeEventListener('click', this.aufKlick);
    this.video?.removeEventListener('ended', this.aufEnde);
  }
}
anmelden('fu-st-video', FuVideo);

/* ── fu-st-newsletter ──────────────────────────────────────────────────────
 * Zustaende des Anmeldeformulars — fuer BEIDE Anmeldewege der Sektion.
 * Welcher laeuft, steht am Host: ohne data-fu-weg der Shopify-Weg, mit
 * data-fu-weg="klaviyo" der Klaviyo-Weg.
 *
 * SHOPIFY-WEG (Vorgabe). Erfolg und Fehler kommen serverseitig zurueck (das
 * Liquid prueft form.posted_successfully? und form.errors und setzt das
 * passende data-fu-zustand). Die Eingabe prueft vor dem Absenden der Browser
 * selbst (type="email", required), damit kostet ein Tippfehler keinen
 * Seitenwechsel. Dieses Element setzt nach dem Absenden den Fokus auf die
 * Meldung, damit eine Sprachausgabe sie erreicht, und raeumt den
 * Fehlerzustand beim naechsten Tastendruck. An diesem Verhalten aendert der
 * Klaviyo-Zweig nichts.
 *
 * KLAVIYO-WEG. Das <form> hat kein action; das Absenden wird abgefangen und
 * die Adresse per fetch an Klaviyos Client-Endpunkt geschickt. Antwort 202
 * heisst angenommen — die Liste steht auf Double-Opt-in, die
 * Bestaetigungsmail verschickt Klaviyo selbst. Danach setzt das Element
 * dieselben data-fu-zustand-Werte, die sonst der Server liefert; Meldungen,
 * CSS und Fokus sind damit fuer beide Wege dieselben.
 *
 * Alles ausser 2xx und jeder Netzfehler fuehren in den Fehlerzustand. Eine
 * Anmeldung laeuft immer nur einmal (this.sendet), und ein laufender Aufruf
 * wird beim Ausbau des Elements abgebrochen — der Theme-Editor rendert die
 * Sektion bei jeder Aenderung neu, sonst schriebe die Antwort in ein
 * Element, das im Dokument gar nicht mehr steht.
 *
 * EINWILLIGUNG. Der Einwilligungstext steht hinter dem Host (seine ID in
 * data-fu-einwilligung). Auf Geraeten mit Maus klappt ihn das CSS der Sektion
 * erst beim Fokus auf. Dieses Element sorgt fuer drei Dinge:
 * 1. Einmal offen, bleibt offen (data-fu-offen). Sonst klappte der Text in
 *    Safari beim Klick auf den Link zu (Safari fokussiert Links beim Klick
 *    nicht), und beim Klaviyo-Senden fiele der Fokus mit dem gesperrten
 *    Knopf auf body.
 * 2. Der Text muss beim Absenden zu SEHEN sein, nicht nur im Seitenaufbau
 *    stehen. Bei jedem Fokus im Formular prueft das Element, ob seine
 *    Unterkante im Fenster liegt, und scrollt sonst sofort nach — ohne
 *    Animation: Horizon scrollt sanft, und Tippen bricht sanftes Scrollen ab
 *    (gemessen am 24.09.2026 in Chromium und WebKit). Liegt der Text beim
 *    Absenden trotzdem ausserhalb (etwa: Feld per Autofill gefuellt, direkter
 *    Klick auf den Knopf in Safari), faengt das Element genau dieses eine
 *    Absenden ab und scrollt; das naechste geht durch.
 * 3. Das Feld springt beim Aufklappen nicht (siehe abstandMessen).
 *
 * KEINE Beschriftung steht hier im Code, auch nicht die des sendenden
 * Knopfes: sie kommt aus data-label-laden am Host.
 */

/* Klaviyo datiert seine API-Versionen; der revision-Header ist Pflicht.
   Geprueft am 17.09.2026 an Klaviyos Changelog — 2026-07-15 ist die aktuelle
   GA-Revision. Ein veralteter Wert faellt spaeter lautlos aus, nicht mit
   einem Knall: Wer Klaviyo anfasst, prueft dieses Datum mit. */
const KLAVIYO_ENDPUNKT = 'https://a.klaviyo.com/client/subscriptions/';
const KLAVIYO_REVISION = '2026-07-15';

/* Konto und Liste. Oeffentliche Schluessel, kein Geheimnis — und Protokoll
   wie die Revision, deshalb hier statt im Theme-Editor: Dort standen sie bis
   24.09.2026 als Einstellungen, und Translate & Adapt hatte sie in fuenf
   Sprachen "uebersetzt" (eine geaenderte ID haette nur auf Deutsch
   gewirkt). TALtd6 = Funduino GmbH, T6fdsL = Liste "Newsletter-DE (DOI)"
   mit Double-Opt-in. */
const KLAVIYO_FIRMA = 'TALtd6';
const KLAVIYO_LISTE = 'T6fdsL';

/* Profilmerkmale, die jede Anmeldung mitbringt. Die erste Weiche des Flows
   "DE-WelcomeSerie-WS" (QXLe6y, Ausloeser T6fdsL) laesst nur Profile mit
   signup = "var1" in die Willkommensserie mit dem Gratis-Versand; ohne das
   Merkmal endete der Flow sofort (gemessen 06.10.2026). Klaviyo schreibt es
   schon beim Absenden ins Profil, die Weiche sieht es nach der Bestaetigung.
   Name und Wert muessen genau so im Flow stehen — wer einen der beiden
   aendert, aendert beide. */
const KLAVIYO_MERKMALE = { signup: 'var1' };

/* So weit bleibt der Einwilligungstext beim Nachscrollen vom unteren
   Fensterrand weg: Platz fuer Shopifys hCaptcha-Plakette (55px hoch, 20px
   ueber dem Rand), die das Kundenformular unten rechts einblendet. */
const EINWILLIGUNG_ABSTAND = 80;

class FuNewsletter extends HTMLElement {
  connectedCallback() {
    this.formular = this.querySelector('form');
    this.feld = this.querySelector('input[type="email"]');
    this.erfolg = this.querySelector('[data-fu-erfolg]');
    this.fehler = this.querySelector('[data-fu-fehler]');
    this.knopf = this.querySelector('[data-fu-knopf]') || this.querySelector('button[type="submit"]');
    this.inhalt = this.parentElement;
    this.einwilligung = document.getElementById(this.getAttribute('data-fu-einwilligung') || '');
    /* Dieselbe Abfrage, an der im CSS das Einklappen haengt. */
    this.maus = window.matchMedia('(hover: hover) and (pointer: fine)');

    /* data-fu-weg setzt das Liquid nur beim Anmeldeweg "klaviyo". */
    this.klaviyo =
      this.getAttribute('data-fu-weg') === 'klaviyo'
        ? { quelle: this.getAttribute('data-fu-quelle') || '' }
        : null;

    /* Ohne dieses Skript taete der Klaviyo-Weg nichts — das Liquid liefert
       den Knopf dort deshalb gesperrt aus (Sektionen-Regel A14). */
    if (this.klaviyo && this.knopf) this.knopf.disabled = false;

    this.aufFokus = (e) => {
      this.einwilligungOeffnen();
      /* Nicht beim Knopf: Chromium und Firefox fokussieren ihn schon beim
         Mausdruck; scrollte die Seite dann, rutschte er unter dem Zeiger weg
         und der Klick ginge ins Leere. Den Knopf deckt der Absende-Stopp ab. */
      if (e.target !== this.knopf) this.einwilligungZeigen();
    };
    this.addEventListener('focusin', this.aufFokus);

    /* Laufend messen, solange die Einwilligung zu ist: Fenster und Seite
       aendern ihre Breite (die Warenkorb-Schublade quetscht die Seite, ohne
       dass sich das Fenster aendert), und der Inhalt der Spalte aendert seine
       Hoehe, ohne dass die Spalte es tut (Schrift laedt nach, Browser stellt
       einen Feldwert wieder her und das CSS klappt auf) — deshalb auch die
       Kinder beobachten. */
    if (this.inhalt && 'ResizeObserver' in window) {
      this.beobachter = new ResizeObserver(() => this.abstandMessen());
      this.beobachter.observe(this.inhalt);
      for (const kind of this.inhalt.children) this.beobachter.observe(kind);
    }

    const zustand = this.getAttribute('data-fu-zustand');
    if (zustand === 'erfolg') this.meldungNachLaden(this.erfolg);
    else if (zustand === 'fehler') this.meldungNachLaden(this.fehler);

    if (!this.formular || !this.feld) return;

    /* Das Modul laedt zurueckgestellt. Hatte das Feld schon vorher Fokus oder
       Wert, kam das focusin zu frueh — dann jetzt nachholen. */
    if (this.contains(document.activeElement) || this.feld.value) this.einwilligungOeffnen();

    this.abbruch = new AbortController();

    this.aufSenden = (e) => {
      /* Sicherung: Heute prueft der Browser selbst (das Formular hat kein
         novalidate) und loest bei ungueltiger Eingabe gar kein submit aus.
         Dieser Zweig greift erst, wenn das einmal anders ist. */
      if (!this.feld.checkValidity()) {
        e.preventDefault();
        this.setAttribute('data-fu-zustand', 'fehler');
        this.feld.setAttribute('aria-invalid', 'true');
        /* Vor reportValidity: Das fokussiert das Feld, und dann soll die
           Beschreibung den Fehler schon tragen. */
        this.fehlerBeschreiben(true);
        if (this.fehler) this.fehler.hidden = false;
        this.feld.reportValidity();
        return;
      }
      /* Einwilligung nicht zu sehen: dieses eine Absenden anhalten, den Text
         zeigen. stopImmediatePropagation haelt auch Shopifys Spam-Schutz an,
         der sich ebenfalls ans submit haengt. Nur einmal je Seitenbesuch,
         damit auch ein Fenster, das den Text nie ganz fasst, absenden kann. */
      if (!this.gezeigt && !this.einwilligungSichtbar()) {
        this.einwilligungOeffnen();
        /* Anhalten nur, wenn es etwas zu zeigen gibt: Der Text klappt noch
           auf, oder das Nachscrollen hat ihn ins Fenster geholt. Passt er
           gar nicht unter den Kopf (sehr starker Zoom), geht das Absenden
           durch, statt einen Tastendruck zu verschlucken. */
        const rahmen = this.einwilligung.parentElement;
        const oeffnet = rahmen && rahmen.clientHeight < this.einwilligung.offsetHeight - 1;
        if (this.einwilligungZeigen() >= 1 || oeffnet) {
          this.gezeigt = true;
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
      }
      /* Shopify-Weg: ab hier macht der Browser weiter wie bisher. */
      if (!this.klaviyo) return;
      e.preventDefault();
      this.anKlaviyo();
    };
    this.formular.addEventListener('submit', this.aufSenden);

    this.aufTippen = () => {
      this.einwilligungOeffnen();
      if (this.getAttribute('data-fu-zustand') !== 'fehler') return;
      this.removeAttribute('data-fu-zustand');
      if (this.fehler) this.fehler.hidden = true;
      /* Ohne Weg-Unterscheidung: Die Fehlermeldung verschwindet in beiden
         Wegen, also darf das Feld nicht als ungueltig stehen bleiben — auch
         das serverseitig gesetzte aria-invalid und der Fehlerbezug des
         Shopify-Weges nicht. */
      this.feld.removeAttribute('aria-invalid');
      this.fehlerBeschreiben(false);
    };
    this.feld.addEventListener('input', this.aufTippen);
  }

  disconnectedCallback() {
    this.abbruch?.abort();
    this.beobachter?.disconnect();
    window.clearTimeout(this.ankerZeit);
    if (this.aufBannerZu) document.removeEventListener('focusout', this.aufBannerZu);
    this.removeEventListener('focusin', this.aufFokus);
    this.formular?.removeEventListener('submit', this.aufSenden);
    this.feld?.removeEventListener('input', this.aufTippen);
  }

  /** Einwilligung dauerhaft offen halten. */
  einwilligungOeffnen() {
    if (this.hasAttribute('data-fu-offen')) return;
    /* Den im eingeklappten Zustand gemessenen Abstand festhalten, BEVOR das
       CSS aufklappt — also noch in diesem Ereignis, vor dem naechsten
       Layout. Auch 0: Im Fehlerzustand fuellt der Inhalt die Spalte, und
       verschwindet die Meldung beim Tippen, wuechse der Abstandshalter
       sonst und schoebe das Feld nach unten. */
    if (this.oben !== undefined) {
      this.inhalt?.style.setProperty('--fu-st-nl-oben', `${Math.max(0, this.oben)}px`);
    }
    this.setAttribute('data-fu-offen', '');
    if (this.maus.matches) this.ankerAus();
  }

  /** Den Abstand ueber dem Inhalt messen, solange die Einwilligung zu ist.
      Ab 900px steht der Inhalt der Textspalte mittig: Ein Abstandshalter oben
      und der Einwilligungsbereich unten teilen sich den freien Platz. Reicht
      der untere Teil nicht fuer den Text, schrumpfte der obere beim
      Aufklappen, und Feld und Knopf rueckten unter dem Mauszeiger nach oben
      (bis rund 50px zwischen 1100 und 1500px, gemessen am 24.09.2026). Mit
      dem festgehaltenen Wert (--fu-st-nl-oben, im CSS min- und max-height des
      Abstandshalters) waechst die Spalte stattdessen nach unten. Gemessen
      wird vorab, nicht erst beim Fokus: Bei reduzierter Bewegung ist die
      Einwilligung im focusin schon offen. Unter 900px hat die Variable keine
      Wirkung. */
  abstandMessen() {
    if (!this.inhalt) return;
    const breite = this.inhalt.clientWidth;
    const breiteNeu = this.breite !== undefined && breite !== this.breite;
    this.breite = breite;
    if (this.hasAttribute('data-fu-offen')) {
      /* Offen gibt es nichts Eingeklapptes mehr zu messen. Aendert sich die
         Breite, passt der alte Wert nicht mehr — loslassen. */
      if (breiteNeu) this.inhalt.style.removeProperty('--fu-st-nl-oben');
      return;
    }
    const erstes = this.inhalt.firstElementChild;
    if (!erstes) return;
    this.oben =
      erstes.getBoundingClientRect().top -
      this.inhalt.getBoundingClientRect().top -
      parseFloat(getComputedStyle(this.inhalt).paddingTop);
  }

  /** Ab 990px scrollt nicht das Fenster, sondern Horizons .page-wrapper
      (assets/base.css). */
  scrollBehaelter() {
    const wrapper = this.closest('.page-wrapper');
    return wrapper &&
      wrapper.scrollHeight > wrapper.clientHeight + 1 &&
      /auto|scroll/.test(getComputedStyle(wrapper).overflowY)
      ? wrapper
      : document.scrollingElement || document.documentElement;
  }

  /** Unterkante des klebenden Kopfs im Fenster, gemessen: Es kleben die
      Sektionshuellen der Kopfgruppe (.header-section, ab 750px auch
      .fu-mega-menu-section). Nicht scroll-padding-top — das rechnet die
      wegscrollende Aktionsleiste und 24px Luft mit (assets/fu-custom.css) und
      belegt bei 250 % Zoom mehr als die halbe Fensterhoehe. */
  kopfUnten() {
    let unten = 0;
    for (const el of document.querySelectorAll('.shopify-section-group-header-group')) {
      const pos = getComputedStyle(el).position;
      if (pos !== 'sticky' && pos !== 'fixed') continue;
      const r = el.getBoundingClientRect();
      if (r.height > 0 && r.top <= 1 && r.bottom > unten) unten = r.bottom;
    }
    return unten;
  }

  /** Scroll-Verankerung kurz aus, waehrend die Einwilligung aufklappt: Sie
      schoebe die Seite sonst um deren Hoehe weiter und das Feld aus dem
      Fenster (gemessen bei 250 und 400 % Zoom, Chromium und Firefox). */
  ankerAus() {
    const ziel = this.scrollBehaelter();
    ziel.style.overflowAnchor = 'none';
    window.clearTimeout(this.ankerZeit);
    this.ankerZeit = window.setTimeout(() => ziel.style.removeProperty('overflow-anchor'), 400);
  }

  /** Ist der Einwilligungstext aufgeklappt und ganz im Fenster, zwischen Kopf
      und unterem Rand? Auf Touch-Geraeten klappt er nicht ein; dort gilt er
      als gezeigt. */
  einwilligungSichtbar() {
    if (!this.einwilligung || !this.maus.matches) return true;
    if (getComputedStyle(this.einwilligung).visibility !== 'visible') return false;
    /* Noch mitten im Aufklappen (die ersten 200ms): Der Rahmen ist niedriger
       als der Absatz darin. */
    const rahmen = this.einwilligung.parentElement;
    if (rahmen && rahmen.clientHeight < this.einwilligung.offsetHeight - 1) return false;
    const r = this.einwilligung.getBoundingClientRect();
    return r.top >= this.kopfUnten() && r.bottom <= window.innerHeight;
  }

  /** Den Einwilligungstext ins Fenster holen: hoch, wenn er unten herausragt,
      runter, wenn er unter dem Kopf liegt. Seine Lage steht schon eingeklappt
      fest (der Absatz hat seine volle Hoehe, nur sein Rahmen ist zu), also
      darf sofort gemessen werden. Nie so weit, dass das fokussierte Element
      unter den Kopf rutscht (starker Zoom, niedriges Fenster): Der Fokus geht
      vor — beim Absenden haelt der Stopp ohnehin einmal an. */
  einwilligungZeigen() {
    if (!this.einwilligung || !this.maus.matches) return 0;
    if (this.getAttribute('data-fu-zustand') === 'erfolg') return 0;
    const r = this.einwilligung.getBoundingClientRect();
    const kopf = this.kopfUnten();
    const aktiv = this.contains(document.activeElement) ? document.activeElement : this.feld;
    const aktivOben = aktiv ? aktiv.getBoundingClientRect().top : r.top;
    let weg = 0;
    if (r.bottom + EINWILLIGUNG_ABSTAND > window.innerHeight) {
      weg = Math.max(0, Math.min(r.bottom + EINWILLIGUNG_ABSTAND - window.innerHeight, aktivOben - kopf - 8));
    } else if (r.top < kopf) {
      /* Nach unten: Bezug ist der hoehere der beiden Punkte, sonst laege das
         Feld (unter 1024px steht der Knopf dazwischen) unter dem Kopf. */
      weg = Math.min(r.top, aktivOben) - kopf - 8;
    }
    if (Math.abs(weg) < 1) return 0;
    const ziel = this.scrollBehaelter();
    /* Beide Behaelter tragen scroll-behavior: smooth — fuer diesen einen
       Schritt aussetzen; Tippen braeche sanftes Scrollen ab. */
    const vorher = ziel.style.scrollBehavior;
    const start = ziel.scrollTop;
    ziel.style.scrollBehavior = 'auto';
    ziel.scrollTop += weg;
    ziel.style.scrollBehavior = vorher;
    this.ankerAus();
    return Math.abs(ziel.scrollTop - start);
  }

  /** Die Fehlermeldung als erste Beschreibung des Felds ein- oder austragen. */
  fehlerBeschreiben(an) {
    if (!this.feld || !this.fehler?.id) return;
    const ids = (this.feld.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter((id) => id && id !== this.fehler.id);
    if (an) ids.unshift(this.fehler.id);
    if (ids.length) this.feld.setAttribute('aria-describedby', ids.join(' '));
    else this.feld.removeAttribute('aria-describedby');
  }

  /** Blendet eine der beiden Meldungen ein und holt den Fokus dorthin. */
  meldungZeigen(el, { scrollen = false } = {}) {
    if (!el) return;
    el.hidden = false;
    el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: !scrollen });
  }

  /** Meldung nach dem Absenden im Shopify-Weg. Die Seite kommt mit dem Anker
      #contact_form zurueck, und der Sprung dorthin nimmt einen frueher
      gesetzten Fokus wieder weg (gemessen am 24.09.2026: Fokus auf der
      Meldung nach 654ms, weg nach 759ms). Deshalb erst nach dem Laden
      fokussieren. Hat der Besucher bis dahin selbst gescrollt oder etwas
      bedient, ihn nicht zurueckholen (den Sprung zur Meldung hat der Anker
      erledigt) — gemerkt an seinen Eingaben, nicht an der Scrollposition,
      denn die aendert auch der Ankersprung. Haelt ein anderes Element den
      Fokus — meist das Einwilligungsbanner —, einmal warten, bis es ihn
      abgibt, und nur dann uebernehmen, wenn er auf die Seite zurueckfaellt. */
  meldungNachLaden(el) {
    if (!el) return;
    el.hidden = false;
    let bewegt = false;
    const merken = () => {
      bewegt = true;
    };
    const eingaben = ['wheel', 'touchmove', 'keydown', 'pointerdown'];
    for (const art of eingaben) window.addEventListener(art, merken, { passive: true, capture: true });
    const fokus = () => {
      for (const art of eingaben) window.removeEventListener(art, merken, { capture: true });
      const aktiv = document.activeElement;
      if (aktiv && aktiv !== document.body && !this.contains(aktiv)) {
        this.aufBannerZu = (ev) => {
          if (ev.target !== aktiv && !aktiv.contains(ev.target)) return;
          document.removeEventListener('focusout', this.aufBannerZu);
          this.aufBannerZu = null;
          requestAnimationFrame(() => {
            if (!document.activeElement || document.activeElement === document.body) this.meldungZeigen(el);
          });
        };
        document.addEventListener('focusout', this.aufBannerZu);
        return;
      }
      this.meldungZeigen(el, { scrollen: !bewegt });
    };
    if (document.readyState === 'complete') requestAnimationFrame(fokus);
    else window.addEventListener('load', () => requestAnimationFrame(fokus), { once: true });
  }

  /** Knopf waehrend des Sendens sperren und umbeschriften. */
  knopfSperren(an) {
    if (!this.knopf) return;
    this.knopf.disabled = an;

    const laden = this.getAttribute('data-label-laden');
    if (!laden) return;
    if (an) {
      if (this.knopfText == null) this.knopfText = this.knopf.textContent;
      this.knopf.textContent = laden;
    } else if (this.knopfText != null) {
      this.knopf.textContent = this.knopfText;
    }
  }

  async anKlaviyo() {
    if (this.sendet) return;
    this.sendet = true;
    this.knopfSperren(true);

    /* Einen alten Fehlerzustand vor dem neuen Versuch raeumen. */
    this.removeAttribute('data-fu-zustand');
    if (this.fehler) this.fehler.hidden = true;
    this.feld.removeAttribute('aria-invalid');
    this.fehlerBeschreiben(false);

    const merkmale = {
      profile: {
        data: {
          type: 'profile',
          attributes: {
            email: this.feld.value.trim(),
            properties: KLAVIYO_MERKMALE,
          },
        },
      },
    };
    if (this.klaviyo.quelle) merkmale.custom_source = this.klaviyo.quelle;

    try {
      const antwort = await fetch(
        `${KLAVIYO_ENDPUNKT}?company_id=${encodeURIComponent(KLAVIYO_FIRMA)}`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/vnd.api+json',
            revision: KLAVIYO_REVISION,
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: merkmale,
              relationships: {
                list: { data: { type: 'list', id: KLAVIYO_LISTE } },
              },
            },
          }),
          signal: this.abbruch.signal,
        }
      );

      /* 202 ist der Erfolgsfall; ok deckt den ganzen 2xx-Bereich ab. */
      if (!antwort.ok) throw new Error(`Klaviyo ${antwort.status}`);

      /* Den festgehaltenen Abstand loslassen: Die Meldung ist hoeher als die
         Formularzeile, und mittig steht sie nur, wenn sich Abstandshalter und
         eingeklappte Einwilligung den Platz wieder teilen. */
      this.inhalt?.style.removeProperty('--fu-st-nl-oben');
      this.setAttribute('data-fu-zustand', 'erfolg');
      this.meldungZeigen(this.erfolg);
    } catch {
      /* Abgebrochen heisst: Das Element ist weg. Dann gibt es niemanden mehr,
         dem eine Fehlermeldung nuetzte. */
      if (this.abbruch.signal.aborted) return;
      this.setAttribute('data-fu-zustand', 'fehler');
      this.feld.setAttribute('aria-invalid', 'true');
      this.fehlerBeschreiben(true);
      this.meldungZeigen(this.fehler);
    } finally {
      this.sendet = false;
      this.knopfSperren(false);
    }
  }
}
anmelden('fu-st-newsletter', FuNewsletter);

/* ── Variantenwaehler in der Produktkachel (21.09.2026) ────────────────────
 * Ein einziger Zuhoerer an document fuer ALLE Kacheln der Seite. Er schreibt
 * beim Wechsel Preis, Streichpreis, Sparpille, Grundpreis, Lieferstatus,
 * Warenkorb-ID und die Produktlinks der Kachel um.
 *
 * WARUM KEIN CUSTOM ELEMENT, obwohl diese Datei sonst nur Custom Elements
 * kennt. Zwei Gruende:
 *   1. Zwoelf Kacheln je Seite waeren zwoelf Hoerer fuer dasselbe Ereignis.
 *   2. Es gibt das Hausmuster schon, und zwar fuer genau dieses Problem:
 *      assets/fu-cross-selling.js:245-293. Dort steht auch die Begruendung
 *      "Der Zuhoerer haengt an document statt an den Zeilen".
 * NICHT der Grund ist eine Doppelbindung beim Morphen — FuAdd nimmt seinen
 * Hoerer in disconnectedCallback wieder ab (Z. 357-360), das kann also gar
 * nicht passieren. Die Annahme stand in einem Entwurf und ist falsch.
 *
 * WARUM ER DEN PREIS SELBST SCHREIBT. Horizons Nachzieher ist in der Kachel
 * tot — nachgemessen, nicht vermutet:
 *   assets/product-price.js:40 steigt aus, sobald das Ereignis aus einer
 *     product-card kommt.
 *   assets/product-card.js:311 greift auf
 *     querySelectorAll("product-price [ref='priceContainer']")[1], also den
 *     ZWEITEN Treffer. Eine Funduino-Kachel enthaelt genau einen. [1] ist
 *     undefined, morph() laeuft nie.
 * Beides faellt still aus: keine Konsolenmeldung, kein Hinweis. Wer sich
 * darauf verlaesst, bekommt lautlos einen stehengebliebenen Preis — und damit
 * die Lage, in der jemand die 68.971-Euro-Ausfuehrung waehlt, waehrend
 * daneben 27.370 steht.
 *
 * KEINE BESCHRIFTUNG UND KEIN FORMAT IM CODE, wie ueberall in dieser Datei.
 * Betraege, die Saetze der Spar-Pille, Grundpreis und Lagertext kommen fertig
 * formatiert aus dem Liquid an den <option>s. Hier wird kein String
 * zusammengebaut.
 *
 * WAS BEIM MORPHEN PASSIERT (Filtern, Sortieren, Seitenblaettern): Die
 * Auswahl faellt auf den Servervorgabewert zurueck (morph.js:326-334 schreibt
 * option.selected vom frisch gerenderten Knoten), und mit ihr Preis, Pille,
 * Lagerzeile und Warenkorb-ID. Alles ZUSAMMEN, also ohne Zwischenzustand.
 * Das ist gewollt. NICHT mit data-skip-node-update "reparieren": Dann behielte
 * das Feld die Wahl des Besuchers, waehrend der Preis daneben zuruecksetzt —
 * genau der Zustand, den dieser Code verhindern soll.
 */
(() => {
  if (window.__fuKachelVariante) return;
  window.__fuKachelVariante = true;

  /* Die vier Stufen aus snippets/fu-lieferstatus.liquid. Klassennamen, keine
     Beschriftungen — die Texte stehen am <option>. */
  const STUFEN = ['in', 'low', 'nach', 'out'];

  /* Leerer Text heisst: Huelle verbergen. Sie bleibt im DOM, damit der
     naechste Wechsel sie wieder befuellen kann — dasselbe Muster wie in
     assets/fu-cross-selling.js:260-264.

     Fehlt der Wertknoten, wird GAR NICHTS getan. Ohne diesen Ausstieg bliebe
     die Huelle sichtbar und truege den alten Betrag: aus "Spare 57 %" wuerde
     beim Wechsel auf eine nicht reduzierte Variante eine Pille, die weiter
     57 % behauptet. Ein halber Preiszustand darf nicht entstehen koennen,
     auch nicht, wenn jemand spaeter ein Attribut umbenennt. */
  const setzen = (huelle, wert, text) => {
    if (!huelle || !wert) return;
    wert.textContent = text;
    huelle.hidden = text === '';
  };

  /* Der Server liefert jeden Waehler hidden aus. Ohne dieses Skript soll
     keiner dastehen — ein natives select wechselt seine Anzeige auch ohne
     JavaScript, der Preis daneben nicht. Die Begruendung steht ausfuehrlich
     im Kopf von snippets/fu-karte-fuss.liquid. */
  const freigeben = () => {
    document.querySelectorAll('[data-fu-kf-waehler][hidden]').forEach((f) => {
      f.hidden = false;
    });

    /* Dasselbe fuer den Kaufknopf, der ebenfalls gesperrt vom Server kommt.
       FuAdd.connectedCallback gibt ihn beim ersten Laden frei; hier geht es
       um die Kacheln NACH dem Filtern, Sortieren und Seitenblaettern. Morph
       aktualisiert den vorhandenen Knoten an Ort und Stelle, also feuert
       connectedCallback dort kein zweites Mal — das Attribut vom Server
       gewaenne, und die Kachel haette nach dem ersten Filterklick einen
       toten Knopf.

       Zwei Bedingungen, beide noetig:
       data-fu-kaufbar="1" schliesst ausverkaufte Varianten aus. Den Wert
       zieht der Variantenwechsel unten mit, sonst gaebe diese Zeile einen
       ausverkauften Artikel wieder frei.
       :not([data-fu-zustand]) schliesst Knoepfe aus, die gerade mitten im
       Korb-Vorgang stecken: FuAdd sperrt den Knopf dafuer und setzt im
       selben Zug data-fu-zustand am Host. Ohne diesen Ausschluss koennte
       eine beliebige DOM-Aenderung waehrend des Ladens den Knopf wieder
       oeffnen — ein zweiter Klick legte dann ein zweites Mal in den Korb. */
    document
      .querySelectorAll('fu-st-add[data-fu-kaufbar="1"]:not([data-fu-zustand]) [data-fu-add][disabled]')
      .forEach((k) => {
        k.disabled = false;
      });
  };

  const uebernehmen = (feld, ansagen) => {
    const fuss = feld.closest('[data-fu-kf]');
    const opt = feld.selectedOptions && feld.selectedOptions[0];
    if (!fuss || !opt) return;

    const d = opt.dataset;
    const preis = d.preis || '';
    const alt = d.alt || '';

    /* ── 1. Preiszeile ───────────────────────────────────────────────────
       Ein einziger Wert entscheidet ueber Farbe, Vorsatz und Streichpreis:
       data-alt. Ist er leer, ist die Variante nicht reduziert — dann muss
       alles drei weg, sonst kaeme alles drei zurueck. So kann der Zustand
       nicht halb umschlagen.

       Die Pille haengt dagegen an data-spare-voll und NICHT an data-alt: Ob
       sie erscheint und ob sie Betrag oder Prozent zeigt, entscheidet
       snippets/fu-spare.liquid (Mindestgrenzen, 100-Euro-Regel). Eine
       reduzierte Variante unter den Grenzen hat Streichpreis und roten
       Preis, aber keine Pille. */
    const zeile = fuss.querySelector('[data-fu-preis]');
    if (zeile) {
      zeile.classList.toggle('fu-preis--sale', alt !== '');

      const betrag = zeile.querySelector('[data-fu-preis-wert]');
      if (betrag) betrag.textContent = preis;

      const vorsatz = zeile.querySelector('[data-fu-preis-vorsatz]');
      if (vorsatz) vorsatz.hidden = alt === '';

      setzen(
        zeile.querySelector('[data-fu-preis-alt]'),
        zeile.querySelector('[data-fu-preis-alt-wert]'),
        alt
      );
      /* Voll- und Kurzform kommen als fertige Saetze vom Server
         (fu-spare, ausgabe: 'attribute' an der <option>): "Spare 25 €" und
         "−25 €". Hier wird nichts zusammengesetzt. Welche der beiden man
         sieht, entscheidet die Container-Abfrage in fu-custom.css. Die Art
         (betrag/prozent) wandert als Attribut mit; beim Morphen setzt der
         Server sie zurueck, zusammen mit allem anderen.

         Fehlt einer der Kind-Knoten, wird die Pille VERBORGEN: Streichpreis
         und Preis stehen an dieser Stelle schon auf der neuen Variante, eine
         stehengebliebene Pille nennte die Ersparnis der vorigen. Lieber
         keine Pille als eine falsche. */
      const pille = zeile.querySelector('[data-fu-preis-spare]');
      const voll = pille && pille.querySelector('[data-fu-preis-spare-voll]');
      const kurz = pille && pille.querySelector('[data-fu-preis-spare-kurz]');
      if (pille && voll && kurz) {
        const satz = d.spareVoll || '';
        voll.textContent = satz;
        kurz.textContent = d.spareKurz || '';
        pille.setAttribute('data-fu-spare-art', d.spareArt || '');
        pille.hidden = satz === '';
      } else if (pille) {
        pille.hidden = true;
      }
    }

    /* ── 2. Grundpreis ───────────────────────────────────────────────────
       Gehoert zur Ausfuehrung, nicht zum Artikel: "100 g" und "500 g" haben
       verschiedene Preise je Kilogramm. Bliebe er stehen, gehoerte die
       Pflichtangabe zur falschen Variante. */
    const grund = fuss.querySelector('[data-fu-kf-grundpreis]');
    if (grund) {
      grund.textContent = d.grundpreis || '';
      grund.hidden = !d.grundpreis;
    }

    /* ── 3. Lieferstatus ─────────────────────────────────────────────────
       Muss mit. Bestand und Verkaufsregel haengen an der VARIANTE: Unitree
       EDU-3 und EDU-4 haben Bestand 0 und sind trotzdem kaufbar. Bliebe die
       Zeile stehen, verspraeche die Kachel "Sofort versandfertig" fuer Ware,
       die nicht da ist. */
    const lager = fuss.querySelector('.fu-lager');
    if (lager) {
      const stufe = d.lagerArt || '';
      const text = d.lagerText || '';
      STUFEN.forEach((s) => lager.classList.toggle('fu-lager--' + s, s === stufe));
      const huelle = lager.querySelector('.fu-lager__text');
      if (huelle) huelle.textContent = text;
      lager.hidden = text === '';
    }

    /* ── 4. Kaufknopf ────────────────────────────────────────────────────
       Mehr als das Attribut ist nicht noetig: FuAdd liest die ID bei JEDEM
       Klick frisch (Z. 387), nichts wird beim Verbinden gemerkt.

       Beim Sperren wandert auch die Beschriftung mit. Sonst meldete eine
       Sprachausgabe einen abgeblendeten Knopf, der weiter "In den Warenkorb"
       heisst — ohne zu sagen, warum er nicht geht. */
    const add = fuss.querySelector('fu-st-add');
    if (add) {
      add.setAttribute('data-variante-id', opt.value);
      const knopf = add.querySelector('[data-fu-add]');
      if (knopf) {
        const kaufbar = d.kaufbar === '1';
        /* Der Wert muss auch an den Host, nicht nur an den Knopf: freigeben()
           entscheidet danach. Ohne diese Zeile gaebe der naechste Morph oder
           irgendeine DOM-Aenderung eine gerade gewaehlte ausverkaufte
           Ausfuehrung wieder frei. */
        add.setAttribute('data-fu-kaufbar', kaufbar ? '1' : '0');
        knopf.disabled = !kaufbar;
        const label = kaufbar ? add.getAttribute('data-label-add') : add.getAttribute('data-label-aus');
        if (label) knopf.setAttribute('aria-label', label);
      }
    }

    /* ── 5. Die Links der Kachel ─────────────────────────────────────────
       Ohne das zeigte die Produktseite nach einem Klick wieder die
       Vorgabevariante — derselbe Preisbruch, eine Ebene tiefer.

       ZWEI Kachelarten, deshalb zwei Wahlausdruecke (22.09.2026). Auf den
       Kategorieseiten, auf /collections/all und in der Suche steckt der Fuss
       in Horizons <product-card>. Die eigenen Produktlisten der Startseite
       haben das nicht — ihre Kachel ist ein nacktes <li ... data-fu-karte>
       (sections/fu-bestseller.liquid:269). Dort lief closest('product-card')
       ins Leere, und zwar STILL: Preis, Sparpille, Grundpreis und Lagerzeile
       zogen beim Wechsel mit, die Links nicht. Wer in der Bestseller-Kachel
       eine Ausfuehrung waehlte und dann auf Bild oder Titel klickte, landete
       auf der Vorgabevariante — beim Bundle auf 94,80 EUR, nachdem die Kachel
       eben noch 164,80 EUR gezeigt hatte. Genau der halbe Zustand, gegen den
       der Kartenfuss gebaut ist. Herein kam das am 21.09.2026 mit der
       Umstellung der Sektion auf den gemeinsamen Fuss.

       data-fu-karte ist der richtige Anker: Es sitzt an JEDER einzelnen
       Kachel und nie um mehrere herum, sonst bekaemen Nachbarprodukte die
       fremde Varianten-ID an ihre Links. sections/fu-kategorien.liquid
       traegt es zwar auch, enthaelt aber keinen einzigen fu-kf-Fuss — von
       dort aus kann closest() also gar nicht erst dorthin laufen. */
    const karte = fuss.closest('product-card, [data-fu-karte]');
    if (karte) {
      karte.querySelectorAll('a[href]').forEach((a) => {
        if (a.pathname.indexOf('/products/') === -1) return;
        try {
          const url = new URL(a.href);
          url.searchParams.set('variant', opt.value);
          a.href = url.toString();
        } catch (e) {
          /* Ein Link ohne aufloesbare Adresse bleibt, wie er ist. */
        }
      });
    }

    /* ── 6. Ansage ───────────────────────────────────────────────────────
       Ein select meldet beim Wechsel nur seinen eigenen neuen Wert. Preis,
       Streichpreis und Pille werden per textContent ersetzt — das passiert
       lautlos (WCAG 4.1.3). Nur beim echten Wechsel ansagen, nicht beim
       Freigeben oder beim Wiederherstellen durch den Browser. */
    if (ansagen) {
      const ansage = fuss.querySelector('[data-fu-kf-ansage]');
      if (ansage) ansage.textContent = (d.name || '') + ', ' + preis;
    }
  };

  document.addEventListener('change', (e) => {
    const ziel = e.target;
    if (!ziel || !ziel.closest) return;
    const feld = ziel.closest('[data-fu-kf-waehler]');
    if (!feld) return;
    uebernehmen(feld, true);
  });

  freigeben();

  /* Nach dem Filtern, Sortieren oder Seitenblaettern liefert der Server die
     Kacheln neu — und damit die Waehler wieder hidden. Ohne das hier waere
     die Kachel nach dem ersten Filterklick ohne Waehler.

     Ein Ereignis dafuer gibt es nicht: assets/facets.js ruft
     sectionRenderer.renderSection() direkt, und morph feuert nichts. Deshalb
     ein Beobachter. Er sieht nur zwei Dinge — neue Knoten und das
     Wiederauftauchen von hidden oder disabled — und freigeben() fasst
     ausschliesslich Elemente an, die das Attribut TRAGEN und es danach nicht
     mehr tragen. Eine Endlosschleife kann daraus nicht entstehen.

     disabled steht seit dem 22.09.2026 mit in der Liste: Der Kaufknopf des
     Kartenfusses kommt gesperrt vom Server und braucht nach jedem Morph
     dieselbe Freigabe wie der Waehler. */
  if (window.MutationObserver) {
    new MutationObserver((eintraege) => {
      for (const e of eintraege) {
        if (e.type === 'attributes') {
          if (e.target.hasAttribute(e.attributeName)) {
            freigeben();
            return;
          }
        } else if (e.addedNodes.length) {
          freigeben();
          return;
        }
      }
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'disabled'],
    });
  }

  /* Browser stellen Formularwerte beim Zurueck-Navigieren wieder her — OHNE
     change zu feuern. Das Feld staende dann auf der zuvor gewaehlten Variante,
     der Preis daneben auf der Vorgabe. autocomplete="off" am Feld ist die
     erste Sicherung, das hier die zweite.

     defaultSelected liest das selected-ATTRIBUT, also das, was der Server
     markiert hat. Der Vergleich trifft deshalb genau die Faelle, in denen
     Anzeige und Auswahl auseinanderstehen, und laeuft beim normalen
     Seitenaufruf ins Leere. Ohne Ansage: Es hat niemand etwas gewaehlt. */
  window.addEventListener('pageshow', () => {
    freigeben();
    document.querySelectorAll('[data-fu-kf-waehler]').forEach((feld) => {
      const opt = feld.selectedOptions && feld.selectedOptions[0];
      if (opt && !opt.defaultSelected) uebernehmen(feld, false);
    });
  });
})();
